/**
 * search.ts — the few search constants and shapes that api/ and web/ must agree on.
 *
 * WHY THESE LIVE HERE: the global search field (`DS-F16`–`DS-F20`) is the one feature where the
 * browser and the server have to apply the SAME rule to the same text at the same moment. The field
 * decides whether to ask for suggestions; the server decides whether to answer. If those two
 * disagree about the minimum query length, the field either asks for suggestions the server will
 * always refuse (a request per keystroke, answered with nothing) or withholds a query the server
 * would happily have answered. Neither fails loudly — they just make the field feel broken.
 *
 * So the threshold is defined ONCE, here, and imported by both sides.
 */
/**
 * DS §8: "Minimum query length — **2** characters". Below this, searching is not attempted on either
 * side: the field does not request suggestions and the server returns none.
 */
export const MIN_QUERY_LENGTH = 2;
/**
 * DS §8: "Results per page — **20**, then load more."
 *
 * A FALLBACK ONLY. The live value is `results_per_page` in `app.platform_setting`, which the platform
 * owner tunes with no deploy (`PC-F20`). Nothing may hardcode a page size in place of reading that
 * setting; this exists so a client with no server answer yet still shows a sane number.
 */
export const DEFAULT_RESULTS_PER_PAGE = 20;
/** DS-F20: "offer up to **five** closest-matching tools beneath the field". */
export const MAX_SUGGESTIONS = 5;
/** DS-F20: "and up to **five** closest-matching people", counted separately from the tools. */
export const MAX_PEOPLE_SUGGESTIONS = 5;
/**
 * The size ceiling, in characters, for inlining an avatar into a suggestion payload.
 *
 * Avatars are stored as `data:` URIs, not files — there is no blob route to point an `<img>` at (see
 * migration 0021: "we do NOT introduce a BlobStore"). `user_self_declared.avatar_blob_ref` is capped at
 * 300,000 characters, and `user_profile.photo_blob_ref` (the directory photo) is capped at NOTHING at
 * all, so a large corporate photo is the realistic worst case rather than an upload.
 *
 * Five people at the 300,000 ceiling would be ~1.4 MB of base64 on EVERY keystroke, with no caching,
 * because the response is freshly generated JSON. So anything above this threshold is sent as `null`
 * and the client falls back to a monogram of the person's initials — which is already what the profile
 * page does for anyone without an avatar. A person's full-size photo still renders on their profile
 * page, where exactly one person is being loaded and the size does not matter.
 *
 * Shared so the two sides cannot disagree: the server decides what to send, the client decides what to
 * render, and both must apply the same rule or a truncated URI could reach an `<img src>`.
 */
export const AVATAR_INLINE_MAX_CHARS = 40_000;
/**
 * The ONLY `data:` URI shapes that may be sent to, or rendered by, a client as an avatar: a base64
 * image of an allowlisted MIME type.
 *
 * SECURITY — this is not a formatting nicety. `avatar_blob_ref` and `photo_blob_ref` are TEXT columns,
 * so a malformed or hostile value could hold `data:text/html,<script>…`, a script-bearing
 * `data:image/svg+xml`, or a `javascript:`/`http(s):` URL. Any of those reaching an `<img src>` is an
 * XSS or a tracking beacon. Checking merely that a string starts with `data:` is NOT sufficient, which
 * is exactly the mistake this constant exists to prevent.
 *
 * It mirrors `AVATAR_UPLOAD_URI_RE` in `web/src/Profile.tsx` and the server's own upload validation.
 * Defined in the shared package so the suggestion payload, the profile page and the upload route
 * cannot drift into three different opinions about what is a safe image.
 */
export const AVATAR_DATA_URI_RE = /^data:image\/(?:webp|png|jpeg);base64,[A-Za-z0-9+/]+={0,2}$/;
/**
 * The role markers a person result may display (`PR-F24`).
 *
 * ONLY these two. `app.appointment` also holds `Reviewer`, `Manager` and `PlatformOwner`, which are
 * ACCESS roles with no profile marker — showing them would leak who can approve what. `UR-F08` also
 * requires markers to be resolved from a live appointment at the moment of use, never from a stored
 * flag, so this type describes what was resolved for one request and is never persisted.
 */
export const PERSON_MARKERS = ['Champion', 'SWAT'];
/**
 * Is this raw input worth searching for?
 *
 * Shared so the field and the server answer identically. Trims first, because " a " is one character
 * of query and two of whitespace — a person who has typed only spaces has not typed a search.
 */
export function isSearchable(raw) {
    return typeof raw === 'string' && raw.trim().length >= MIN_QUERY_LENGTH;
}
//# sourceMappingURL=search.js.map